import { jsx, jsxs } from "react/jsx-runtime";
import { getRangeLength, getSequenceWithinRange } from "@teselagen/range-utils";
import React from "react";
import { divideBy3 } from "./proteinUtils";
export function getSanitizedId(id) {
  return String(id).replace(/ /g, "_");
}
import {
  getInsertBetweenVals,
  calculatePercentGC,
  aliasedEnzymesByName
} from "@teselagen/sequence-utils";
import { get, sortBy } from "lodash-es";
import VeWarning from "../helperComponents/VeWarning";
import { normalizePositionByRangeLength } from "@teselagen/range-utils";
import { filter } from "lodash-es";
export function getSelectionMessage({
  caretPosition = -1,
  selectionLayer = { start: -1, end: -1 },
  customTitle,
  sequenceLength,
  sequenceData,
  showGCContent,
  //these are only passed in for the status bar
  GCDecimalDigits,
  //these are only passed in for the status bar
  isProtein,
  showAminoAcidUnitAsCodon
}) {
  let _selectionLayer = selectionLayer;
  const isSelecting = selectionLayer.start > -1;
  if (isSelecting) {
    _selectionLayer = getSelFromWrappedAddon(selectionLayer, sequenceLength);
    const length = getRangeLength(_selectionLayer, sequenceLength);
    const GCContent = (numDecimalDigits = 0) => calculatePercentGC(
      getSequenceWithinRange(_selectionLayer, sequenceData.sequence)
    ).toFixed(numDecimalDigits);
    const seqLen = divideBy3(length, isProtein);
    return `${customTitle || "Selecting"} ${seqLen} ${(isProtein ? `${showAminoAcidUnitAsCodon ? "codon" : "AA"}` : "bp") + (seqLen === 1 ? "" : "s")} from ${divideBy3(_selectionLayer.start, isProtein) + 1} to ${divideBy3(
      _selectionLayer.end + 1,
      isProtein
    )}${showGCContent && !isProtein ? ` (${GCContent(GCDecimalDigits)}% GC)` : ""}`;
  } else if (caretPosition > -1) {
    const insertBetween = getInsertBetweenVals(
      caretPosition,
      _selectionLayer,
      sequenceLength
    );
    return `Caret Between ` + (isProtein ? `${showAminoAcidUnitAsCodon ? "codons" : "AAs"} ${divideBy3(insertBetween[0], true)} and ${divideBy3(
      insertBetween[1] + 2,
      true
    )}` : `Bases ${insertBetween[0]} and ${insertBetween[1]}`);
  } else {
    return "No Selection";
  }
}
export function preventDefaultStopPropagation(e) {
  if (e) {
    e.stopPropagation();
    e.preventDefault();
  }
}
export function getNodeToRefocus(caretEl) {
  let nodeToReFocus;
  if (caretEl && caretEl.closest && caretEl.closest(".veVectorInteractionWrapper")) {
    nodeToReFocus = caretEl.closest(".veVectorInteractionWrapper");
  }
  return nodeToReFocus;
}
export function getEmptyText({ sequenceData, caretPosition }) {
  return sequenceData.sequence.length === 0 && caretPosition === -1 ? /* @__PURE__ */ jsx("div", { className: "veEmptySeqText", children: "Insert Sequence Here" }) : null;
}
export function tryToRefocusEditor() {
  const ed = document.querySelector(".veVectorInteractionWrapper");
  ed && ed.focus();
}
export function getCustomEnzymes() {
  try {
    const customEnzymes = JSON.parse(
      window.localStorage.getItem("customEnzymes") || "{}"
    );
    return customEnzymes;
  } catch (error) {
    return {};
  }
}
export function addCustomEnzyme(newEnz) {
  const customEnzymes = getCustomEnzymes();
  window.localStorage.setItem(
    "customEnzymes",
    JSON.stringify({
      ...customEnzymes,
      [newEnz.name.toLowerCase()]: newEnz
    })
  );
}
export function pareDownAnnotations(annotations, max) {
  let annotationsToPass = annotations;
  let paredDown = false;
  if (!max || max === Infinity) {
    return [annotationsToPass, false];
  }
  if (Object.keys(annotations).length > max) {
    paredDown = true;
    const sortedAnnotations = sortBy(annotations, function(annotation) {
      if (annotation.type === "s_mutation" || annotation.type === "search_mutation") {
        return -1e6;
      }
      return -getRangeLength(annotation);
    });
    annotationsToPass = sortedAnnotations.slice(0, max).reduce(function(obj, item) {
      obj[item.id] = item;
      return obj;
    }, {});
  }
  return [annotationsToPass, paredDown];
}
export function getParedDownWarning({ nameUpper, maxToShow, isAdjustable }) {
  return /* @__PURE__ */ jsx(
    VeWarning,
    {
      "data-test": `ve-warning-max${nameUpper}ToDisplay`,
      tooltip: `Warning: More than ${maxToShow} ${nameUpper === "Cutsites" ? "Cut Sites" : nameUpper}. Only displaying ${maxToShow} ${isAdjustable ? "(Configure this under View > Limits)" : ""} `
    },
    `ve-warning-max${nameUpper}ToDisplay`
  );
}
export function getClientX(event) {
  return event.clientX || get(event, "touches[0].clientX");
}
export function getClientY(event) {
  return event.clientY || get(event, "touches[0].clientY");
}
export function hideAnnByLengthFilter(hideOpts, ann, seqLen) {
  const featLength = getRangeLength(ann, seqLen);
  return hideOpts.enabled && (featLength < hideOpts.min || featLength > hideOpts.max);
}
export function getSelFromWrappedAddon(selectionLayer, sequenceLength) {
  const selToUse = {
    ...selectionLayer
  };
  if (selectionLayer.isWrappedAddon) {
    const oldEnd = selToUse.end;
    selToUse.end = normalizePositionByRangeLength(
      selToUse.start - 1,
      sequenceLength
    );
    selToUse.start = normalizePositionByRangeLength(oldEnd + 1, sequenceLength);
    delete selToUse.isWrappedAddon;
  }
  return selToUse;
}
export function getStripedPattern({ color, id }) {
  const sanitizedId = getSanitizedId(id);
  return /* @__PURE__ */ jsx(
    "pattern",
    {
      id: `diagonalHatch-${sanitizedId}`,
      patternUnits: "userSpaceOnUse",
      width: "4",
      height: "4",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M-1,1 l2,-2\n           M0,4 l4,-4\n           M3,5 l2,-2",
          style: {
            stroke: color,
            strokeWidth: 2,
            fill: "blue",
            opacity: 0.4
          }
        }
      )
    }
  );
}
export function getPrimerGradient({ id, forward }) {
  const isForward = !!forward;
  const topColor = isForward ? "#006600" : "#c2ffc2";
  const shineColor = isForward ? "#009900" : "#e6ffe6";
  const bottomColor = isForward ? "#003300" : "#99e699";
  const sanitizedId = getSanitizedId(id);
  return /* @__PURE__ */ jsxs(
    "linearGradient",
    {
      id: `primerGradient-${sanitizedId}`,
      x1: "0%",
      y1: "0%",
      x2: "0%",
      y2: "100%",
      children: [
        /* @__PURE__ */ jsx("stop", { offset: "0%", stopColor: topColor }),
        /* @__PURE__ */ jsx("stop", { offset: "15%", stopColor: topColor }),
        /* @__PURE__ */ jsx("stop", { offset: "50%", stopColor: shineColor }),
        /* @__PURE__ */ jsx("stop", { offset: "85%", stopColor: bottomColor }),
        /* @__PURE__ */ jsx("stop", { offset: "100%", stopColor: bottomColor })
      ]
    }
  );
}
export const getEnzymeAliases = (enzyme) => {
  let lowerName = enzyme.name && enzyme.name.toLowerCase() || "";
  if (typeof enzyme === "string") {
    lowerName = enzyme.toLowerCase();
  }
  return filter(
    (aliasedEnzymesByName[lowerName] || {}).aliases,
    (n) => n.toLowerCase() !== lowerName
    //filter out current enzyme
  );
};
